import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    ArrowRight,
} from "lucide-react";
import { BASE_URL, loginApi } from "../../../api/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import { syncSsoCookie } from "../../../utils/sso";

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { user, token, login } = useAuth();

    // Already logged in? Send to dashboard instead of showing login.
    if (token) {
        return <Navigate to="/dashboard" replace />;
    }

    // Determine redirect path
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get("redirect") || "/dashboard";

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Remove spaces continuously during typing
        const noSpaceValue = value.replace(/\s/g, "");
        setFormData((prev) => ({ ...prev, [name]: noSpaceValue }));
    };

    const handleKeyDown = (e) => {
        if (e.key === " ") e.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const { email, password } = formData;

            const response = await axios.post(
                `${BASE_URL}${loginApi}`,
                { email, password },
                {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                },
            );

            const resData = response.data;

            if (resData.success) {
                const user = resData.data;
                const token = resData.token;

                if (!token) {
                    toast.error(
                        "Login successful but token not received from backend.",
                    );
                    setIsSubmitting(false);
                    return;
                }

                // Check user status
                if (
                    user.status === "1" ||
                    user.status === "true" ||
                    (typeof user.status === "string" &&
                        user.status.toLowerCase() === "active")
                ) {
                    const sessionUser = { ...user, token };
                    login(sessionUser);

                    // SSO: sync token to test portal so later navigation auto-logs in
                    // Laravel returns sso_url = https://test.pyqpedia.com/sso/login?token=...&next=...
                    // We use it to set first-party sso_token cookie via hidden iframe (AppController::apiTokenSsoBridge)
                    if (resData.sso_url) {
                        syncSsoCookie(resData.sso_url);
                    } else if (token) {
                        syncSsoCookie(token);
                    }

                    toast.success("Login successful!");
                    setTimeout(() => {
                        // If redirect points to the test portal (external), do a full navigation so SSO applies.
                        if (/^https?:\/\//.test(redirectPath)) {
                            window.location.href = redirectPath;
                        } else {
                            navigate(redirectPath);
                        }
                    }, 1000);
                } else {
                    toast.error(
                        "Your account is inactive. Please contact support.",
                    );
                    setIsSubmitting(false);
                }
            } else {
                toast.error(resData.message || "Invalid login credentials");
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error("Login error:", error);
            toast.error(
                error.response?.data?.message ||
                    "Login failed. Please try again.",
            );
            setIsSubmitting(false);
        }
    };

    // Animation variants
    const containerVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1,
            },
        },
    };

    const childVariant = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    };

    return (
        <div className="bg-gray-50 flex flex-col relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-size-[40px_40px]"></div>

            

            <main className="grow flex items-center justify-center relative z-10 w-full">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariant}
                    className="w-full mx-auto flex flex-col lg:flex-row lg:justify-center min-h-[80vh]"
                >
                    {/* LEFT PANEL: Branding & Welcome (Top on Mobile, Left on Desktop) */}
                    <motion.div
                        variants={childVariant}
                        className="w-full aspect-video md:aspect-auto"
                        style={{
                            backgroundImage:
                                "url('/assets/login-illustration.png')",
                            backgroundPosition: "start center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                        }}
                    ></motion.div>

                    {/* RIGHT PANEL: Login Form (Bottom on Mobile, Right on Desktop) */}
                    <motion.div
                        variants={childVariant}
                        className="w-full p-12 flex flex-col justify-center"
                    >
                        <div className="max-w-md w-full mx-auto">
                            <div className="mb-8">
                                <h2 className="text-3xl lg:text-4xl font-bold text-black mb-4 leading-tight">
                                    Welcome{" "}
                                    <span className="text-yellow-500">
                                        Back!
                                    </span>
                                </h2>
                                <p className="text-gray-500 text-sm font-medium">
                                    Enter your credentials to securely access
                                    your account.
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Email Field */}
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-bold text-neutral-900 mb-2"
                                    >
                                        Email Address{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                            <Mail size={18} />
                                        </div>
                                        <input
                                            id="email"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onKeyDown={handleKeyDown}
                                            onChange={handleChange}
                                            placeholder="Enter your email"
                                            required
                                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                        />
                                    </div>
                                </div>

                                {/* Password Field */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label
                                            htmlFor="password"
                                            className="block text-sm font-bold text-neutral-900"
                                        >
                                            Password{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-sm font-medium text-yellow-600 hover:text-yellow-700 transition-colors"
                                        >
                                            Forgot Password?
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                            <Lock size={18} />
                                        </div>
                                        <input
                                            id="password"
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            name="password"
                                            value={formData.password}
                                            onKeyDown={handleKeyDown}
                                            onChange={handleChange}
                                            placeholder="Enter your password"
                                            required
                                            className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword((prev) => !prev)
                                            }
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-neutral-900 transition-colors focus:outline-none"
                                        >
                                            {showPassword ? (
                                                <EyeOff size={18} />
                                            ) : (
                                                <Eye size={18} />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full flex items-center justify-center px-8 py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-xl shadow-sm transition-all duration-300 group disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center">
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white group-hover:text-neutral-900"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Authenticating...
                                            </span>
                                        ) : (
                                            <>
                                                Sign In
                                                <ArrowRight
                                                    size={18}
                                                    className="ml-2 transform group-hover:translate-x-1 transition-transform"
                                                />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Registration Footer */}
                            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                                <p className="text-sm font-medium text-gray-500">
                                    Don't have an account yet?{" "}
                                    <Link
                                        to={`/register?redirect=${encodeURIComponent(redirectPath)}`}
                                        className="font-bold text-neutral-900 hover:text-red-700 transition-colors underline decoration-2 underline-offset-4"
                                    >
                                        Create one now
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </main>

            
        </div>
    );
};

export default Login;
